/*
 * Copyright (c) 2020  Apricad LLC. All Rights Reserved.
 */

import * as React from "react";
import { useState, useEffect } from "react";

interface Props {
  // component props
  pdfDoc: any;
  pdfPageNum: number;
  pdfPageScale: number;
  canvasId?: string;
}

const PdfViewerBase: React.FC<Props> = ({
  pdfDoc,
  pdfPageNum,
  pdfPageScale,
  canvasId,
}: Props) => {
  const [pageRendering, setPageRendering] = useState(false);
  const [pagePending, setPagePending] = useState(0);
  const [canvas, setCanvas] = useState<any | null>(null);
  const [ctx, setCtx] = useState(null);

  const pdfCanvasId = canvasId || "pdf-canvas";

  // When pdfDoc is ready, init zoom scale is calculated, put in render queue
  // When page number or scale change, put in render queue as well.
  useEffect(() => {
    if (pdfDoc && pdfPageScale !== 0) {
      queueRenderPage(pdfPageNum);
    }
  }, [pdfDoc, canvas, pdfPageNum, pdfPageScale]);

  // If there's page pending, and not in rendering, render the pending page.
  useEffect(() => {
    if (pagePending && !pageRendering) {
      renderPage(pagePending);
      setPagePending(0);
    }
  }, [pagePending, pageRendering]);

  /**
   * Get page info from document, resize canvas accordingly, and render page.
   * @param num Page number.
   */
  const renderPage = (num: number) => {
    if (canvas === null) {
      const canvas_: any = document.getElementById(pdfCanvasId);
      if (canvas_) {
        setCanvas(canvas_);
        setCtx(canvas_.getContext("2d"));
      }
    } else {
      setPageRendering(true);
      // Using promise to fetch the page
      pdfDoc.getPage(num).then((page: any) => {
        // [Less Ideal fix for resolution - Part 1]
        // var viewport = page.getViewport({ scale: devicePixelRatio > 1.2 ? pdfPageScale * devicePixelRatio : pdfPageScale });
        // canvas.height = viewport.height; canvas.width = viewport.width;

        const devicePixelRatio = window.devicePixelRatio || 1;

        var viewport = page.getViewport({ scale: pdfPageScale });
        canvas.width = viewport.width * devicePixelRatio;
        canvas.height = viewport.height * devicePixelRatio;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        const transform = [devicePixelRatio, 0, 0, devicePixelRatio, 0, 0];

        // Render PDF page into canvas context
        var renderTask = page.render({
          canvasContext: ctx,
          viewport,
          transform,
        });

        // Wait for rendering to finish
        renderTask.promise.then(() => setPageRendering(false));
      });
    }
  };

  /**
   * If another page rendering in progress, waits until the rendering is
   * finised. Otherwise, executes rendering immediately.
   */
  const queueRenderPage = (num: number) =>
    pageRendering ? setPagePending(num) : renderPage(num);

  // Detect iPad/iPhone. We don't need this anymore.
  //   navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPhone/i);

  return (
    <canvas
      // [Less Ideal fix for resolution - Part 2]
      // style={ devicePixelRatio > 1.2
      //   ? { transformOrigin: "5px 5px", transform: `scale(${1/devicePixelRatio}, ${1/devicePixelRatio})` } : {}  }
      id={pdfCanvasId}></canvas>
  );
};

export const PdfViewer: any = PdfViewerBase;
